import JenApiPlatformIcon from '@/assets/icons/JenApiPlatform.svg';
import { Avatar } from '@/components/Header';
import { bootstrap } from '@/service/user';
import { useRequest } from 'ahooks';
import { useEffect, useState } from 'react';
import { Card, CardContent, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { PrimaryButton } from '@/components/Button';
import BellRingIcon from '@/assets/icons/BellRing.svg';
import { toast } from 'sonner';
import { createOrganization, getOrganization } from '@/service/overview';
import { useNavigate } from 'react-router-dom';

interface FormData {
  name: string;
  contact_name: string;
  email: string;
  termsAgreed: boolean;
  use_case: string;
  expected_volume: string;
}

const Signup = () => {
  return (
    <div className="w-full flex flex-col">
      <Header />
      <UserRegistration />
    </div>
  );
};

const Header = () => {
  const { data: userInfo } = useRequest(bootstrap);

  return (
    <div className="flex justify-between items-center h-[64px] w-full px-4">
      <JenApiPlatformIcon />
      <Avatar name={userInfo?.name} />
    </div>
  );
};

const UserRegistration = () => {
  const [step, setStep] = useState<number>(1);
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    contact_name: '',
    email: '',
    termsAgreed: false,
    use_case: '',
    expected_volume: '',
  });
  const { loading } = useRequest(getOrganization, {
    onSuccess: ({ contact_name }) => {
      if (contact_name) {
        setStep(3);
      }
    },
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSelectChange = (name: keyof FormData, value: string) => {
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const validateStep1 = (): boolean => {
    if (!formData.name) {
      toast.error('Name is required');
      return false;
    }
    if (!formData.contact_name) {
      toast.error('Key Contact Name is required');
      return false;
    }
    if (!formData.email) {
      toast.error('Email is required');
      return false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      toast.error('Email is invalid');
      return false;
    }
    if (!formData.termsAgreed) {
      toast.error('You must agree to the terms');
      return false;
    }
    return true;
  };

  const validateStep2 = (): boolean => {
    if (!formData.use_case) {
      toast.error('Use Case is required');
      return false;
    }
    if (!formData.expected_volume) {
      toast.error('Expected Volume is required');
      return false;
    }
    return true;
  };

  const handleContinue = async () => {
    if (step === 1) {
      if (validateStep1()) {
        setStep(step + 1);
      }
    } else if (step === 2) {
      if (validateStep2()) {
        try {
          await createOrganization({
            name: formData.name,
            contact_name: formData.contact_name,
            email: formData.email,
            use_case: formData.use_case,
            expected_volume: parseInt(formData.expected_volume) || 0,
          });
          setStep(step + 1);
          toast.success('Data submitted successfully!');
        } catch (error) {
          toast.error('Failed to submit data.');
        }
      }
    }
  };

  const progressIndicator = () => {
    return (
      <div className="flex justify-center mb-6">
        <div className={`h-1 w-6 rounded mx-1 ${step >= 1 ? 'bg-jen-orange' : 'bg-gray-200'}`}></div>
        <div className={`h-1 w-6 rounded mx-1 ${step >= 2 ? 'bg-jen-orange' : 'bg-gray-200'}`}></div>
        <div className={`h-1 w-6 rounded mx-1 ${step >= 3 ? 'bg-jen-orange' : 'bg-gray-200'}`}></div>
      </div>
    );
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <Card className="w-full mx-auto">
            <CardContent className="pt-6 flex flex-col gap-8">
              <div>
                <CardTitle className=" ">Welcome to Jen API Platform</CardTitle>
                <CardDescription className="mb-6">
                  In order to verify your account, we’ll need to know a little bit about you.
                </CardDescription>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Individual or Organization Name</Label>
                  <Input id="name" name="name" value={formData.name} onChange={handleInputChange} className="mt-2 h-12" />
                </div>

                <div>
                  <Label htmlFor="contact_name">Key Contact Name</Label>
                  <Input
                    id="contact_name"
                    name="contact_name"
                    value={formData.contact_name}
                    onChange={handleInputChange}
                    className="mt-2 h-12"
                  />
                </div>

                <div>
                  <Label htmlFor="email">Your Email</Label>
                  <Input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} className="mt-2 h-12" />
                </div>

                <div className="flex space-x-2">
                  <Checkbox
                    id="terms"
                    name="termsAgreed"
                    checked={formData.termsAgreed}
                    onCheckedChange={(checked) => setFormData({ ...formData, termsAgreed: checked as boolean })}
                  />
                  <label
                    htmlFor="terms"
                    className="text-sm text-jen-content-tertiary leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    I have read and agree with
                    <a href="https://api.jenmusic.ai/docs/terms-and-conditions" target="_blank" className="block text-jen-orange underline">
                      Jen's Terms and Conditions
                    </a>
                  </label>
                </div>
              </div>
              <PrimaryButton onClick={handleContinue}>Continue</PrimaryButton>
            </CardContent>
          </Card>
        );

      case 2:
        return (
          <Card className="w-full mx-auto">
            <CardContent className="pt-6 flex flex-col gap-8">
              <div>
                <CardTitle className=" mb-4">API Usage Intent</CardTitle>
                <CardDescription className="mb-6">We need a few details in order to verify your API account.</CardDescription>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="use_case">Use Case</Label>
                  <Input
                    name="use_case"
                    value={formData.use_case}
                    onChange={(e) => handleSelectChange('use_case', e.target.value)}
                    className="mt-2 h-12"
                  />
                </div>

                <div>
                  <Label htmlFor="expected_volume">Anticipated Daily Track Generations</Label>
                  <Select onValueChange={(value) => handleSelectChange('expected_volume', value)} defaultValue={formData.expected_volume}>
                    <SelectTrigger className="w-full h-12 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0~1000">&lt;1k</SelectItem>
                      <SelectItem value="1000~10000">1k~10k</SelectItem>
                      <SelectItem value="10000~50000">10k~50k</SelectItem>
                      <SelectItem value="50000+">&gt;50k</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <PrimaryButton onClick={handleContinue}>Continue</PrimaryButton>
            </CardContent>
          </Card>
        );

      case 3:
        return (
          <Card className="w-full mx-auto">
            <CardContent className="pt-6 flex flex-col items-center gap-8">
              <div className="w-full">
                <div className="w-fit rounded-full p-5 bg-[#FF5B271A] inline-block text-left">
                  <BellRingIcon />
                </div>
              </div>

              <div>
                <CardTitle className="">Verification in Progress</CardTitle>
                <CardDescription className="mb-6 text-left">
                  We are reviewing your application and will send you an email to confirm your verification shortly.
                </CardDescription>
              </div>

              <PrimaryButton
                onClick={() => {
                  navigate('/');
                }}
                className="w-full"
              >
                Continue to Dashboard
              </PrimaryButton>
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };
  if (loading) {
    return null;
  }
  return (
    <div className="flex-1 flex flex-col items-center justify-center w-[calc(100vw-30px)] lg:w-[384px] mx-auto">
      {progressIndicator()}
      {renderStep()}
    </div>
  );
};

export default Signup;
